// src/pages/AgendaPage.jsx
// C3.2.4 — Agenda Comercial estilo Lead Web ORBIS
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { API_BASE } from "../config/orbisConfig";



const EMPTY_FORM = {
    id: null,
    account_id: "",
    contact_id: "",
    opportunity_id: "",
    type: "seguimiento_propuesta",
    channel: "whatsapp",
    location: "",
    title: "",
    objective: "",
    result: "",
    status: "programada",
    start_datetime: "",
    end_datetime: "",
    next_action: "",
    next_action_date: "",
    direct_cost: "",
    time_spent_hours: "",
    notes: "",
    alerts: [],
};

const EMPTY_COMPLETE = {
    id: null,
    result: "",
    next_action: "",
    next_action_date: "",
    notes: "",
    create_next_activity: false,
    next_type: "seguimiento_propuesta",
    next_channel: "whatsapp",
};

const TYPE_OPTIONS = [
    ["llamada", "Llamada"],
    ["whatsapp", "WhatsApp"],
    ["correo", "Correo"],
    ["reunion_presencial", "Reunión presencial"],
    ["reunion_virtual", "Reunión virtual"],
    ["demo_orbis", "Demo ORBIS PMO"],
    ["envio_propuesta", "Envío de propuesta"],
    ["seguimiento_propuesta", "Seguimiento de propuesta"],
    ["negociacion", "Negociación"],
    ["cierre_piloto", "Cierre de piloto"],
    ["seguimiento_continuidad", "Seguimiento continuidad"],
    ["otro", "Otro"],
];

const CHANNEL_OPTIONS = [
    ["whatsapp", "WhatsApp"],
    ["email", "Email"],
    ["telefono", "Teléfono"],
    ["presencial", "Presencial"],
    ["meet", "Google Meet"],
    ["zoom", "Zoom"],
    ["linkedin", "LinkedIn"],
    ["otro", "Otro"],
];

const STATUS_OPTIONS = [
    ["programada", "Programada"],
    ["realizada", "Realizada"],
    ["reprogramada", "Reprogramada"],
    ["cancelada", "Cancelada"],
    ["sin_respuesta", "Sin respuesta"],
];

const URGENCY_OPTIONS = [
    ["", "Todas"],
    ["baja", "Baja"],
    ["media", "Media"],
    ["alta", "Alta"],
    ["critica", "Crítica"],
];

const PRODUCT_LINES = ["", "ORBIS PMO", "Afiliaciones CANIETI", "GreenDoc", "SuperLeads", "SITIC", "RH3", "Otro"];

function authHeaders(extra = {}) {
    const token = localStorage.getItem("cani_auth") || "";
    return { ...extra, Authorization: `Bearer ${token}`, "X-CANI-AUTH": token };
}

async function safeJson(url, options = {}) {
    const res = await fetch(url, options);
    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        throw new Error(`Respuesta no JSON (${res.status}): ${text.slice(0, 220)}`);
    }
    if (!res.ok || data?.success === false || data?.ok === false) {
        throw new Error(data?.error || data?.detail || `HTTP ${res.status}`);
    }
    return data;
}

function asArray(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.contacts)) return data.contacts;
    return [];
}

function addDaysYmd(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}
function isValidDateTimeValue(value) {
    if (!value) return false;
    const raw = String(value);
    if (raw.startsWith("0000-00-00")) return false;
    const d = new Date(raw.replace(" ", "T"));
    return !Number.isNaN(d.getTime());
}
function toDateTimeLocal(value) {
    if (!value) return "";
    const raw = String(value);
    if (raw.startsWith("0000-00-00")) return "";

    const normalized = raw.replace(" ", "T").slice(0, 16);
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return "";

    return normalized;
}
function normalizeDateTimeForApi(value) {
    if (!value) return null;
    const v = String(value).replace("T", " ");
    return v.length === 16 ? `${v}:00` : v;
}
function formatDateTime(value) {
    if (!isValidDateTimeValue(value)) return "—";
    const d = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return "—";

    const day = String(d.getDate()).padStart(2, "0");
    const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
    const month = months[d.getMonth()] || "—";
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "p. m." : "a. m.";
    hours = hours % 12 || 12;
    const hour = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} · ${hour}:${minutes} ${ampm}`;
}
function formatCurrency(value, currency = "MXN") {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return `${currency || "MXN"} ${num.toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function toGoogleCalendarDate(value, fallbackMinutes = 0) {
    if (!value) return "";
    const raw = String(value);
    if (raw.startsWith("0000-00-00")) return "";

    const base = new Date(raw.replace(" ", "T"));
    if (Number.isNaN(base.getTime())) return "";
    if (fallbackMinutes) base.setMinutes(base.getMinutes() + fallbackMinutes);
    return base.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function buildGoogleCalendarUrl(activity) {
    if (!activity?.start_datetime) return "";

    const start = toGoogleCalendarDate(activity.start_datetime);
    let end = "";

    if (isValidDateTimeValue(activity.end_datetime)) {
        end = toGoogleCalendarDate(activity.end_datetime);
    }

    if (!end) {
        end = toGoogleCalendarDate(activity.start_datetime, 60);
    }

    if (!start || !end) return "";

    const title = activity.title || "Actividad comercial";
    const details = [
        activity.objective ? `Objetivo: ${activity.objective}` : "",
        activity.result ? `Resultado: ${activity.result}` : "",
        activity.next_action ? `Próxima acción: ${activity.next_action}` : "",
        activity.account_name ? `Cuenta: ${activity.account_name}` : "",
        activity.contact_name ? `Contacto: ${activity.contact_name}` : "",
        activity.opportunity_name ? `Oportunidad: ${activity.opportunity_name}` : "",
        activity.campaign_name ? `Campaña: ${activity.campaign_name}` : "",
        activity.pipeline_stage_name ? `Etapa: ${activity.pipeline_stage_name}` : "",
    ].filter(Boolean).join("\n");

    const params = new URLSearchParams({
        action: "TEMPLATE",
        text: title,
        dates: `${start}/${end}`,
        details,
        location: activity.location || "",
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function openGoogleCalendarEvent(activity, onError) {
    const url = buildGoogleCalendarUrl(activity);

    if (!url) {
        if (typeof onError === "function") {
            onError("Esta actividad no tiene fecha/hora válida para enviarse a Google Calendar.");
        }
        return;
    }

    window.location.href = url;
}

function getActivitySortValue(activity, key) {
    if (!activity) return "";

    const map = {
        date: activity.start_datetime || "",
        status: activity.status || "",
        type: `${activity.type || ""} ${activity.channel || ""}`,
        account: `${activity.account_name || ""} ${activity.contact_name || ""}`,
        opportunity: `${activity.opportunity_name || ""} ${activity.product_line || ""}`,
        campaign: `${activity.campaign_name || ""} ${activity.pipeline_name || ""}`,
        stage: `${activity.stage_order || ""} ${activity.pipeline_stage_name || ""}`,
        value: Number(activity.pilot_value || activity.monthly_potential || activity.estimated_value || 0),
        activity: `${activity.title || ""} ${activity.next_action || ""}`,
    };

    return map[key] ?? "";
}
function labelFromOptions(options, value) {
    return options.find(([v]) => String(v).toLowerCase() === String(value || "").toLowerCase())?.[1] || value || "—";
}
function statusTone(status) {
    if (status === "realizada") return "bg-[#0b3a36] text-[#7cebd0] border-[#10b981]/40";
    if (status === "cancelada") return "bg-[#38161c] text-[#fca5a5] border-[#ef4444]/40";
    if (status === "sin_respuesta") return "bg-[#3a3110] text-[#f6d76b] border-[#d4af37]/45";
    if (status === "reprogramada") return "bg-[#24163d] text-[#c4b5fd] border-[#8b5cf6]/40";
    return "bg-[#0b3552] text-[#7dd3fc] border-[#0ea5e9]/50";
}
function urgencyTone(urgency) {
    const u = String(urgency || "").toLowerCase();
    if (u === "critica") return "bg-[#38161c] text-[#fca5a5] border-[#ef4444]/40";
    if (u === "alta") return "bg-[#3b2413] text-[#fdba74] border-[#f97316]/40";
    if (u === "media") return "bg-[#3a3110] text-[#f6d76b] border-[#d4af37]/45";
    if (u === "baja") return "bg-[#0b3a36] text-[#7cebd0] border-[#10b981]/40";
    return "bg-[#0f2d48] text-[#d9e8f5] border-[#24537a]";
}
function stageTone(stageType) {
    if (stageType === "ganada") return "bg-[#0b3a36] text-[#7cebd0] border-[#10b981]/40";
    if (stageType === "perdida") return "bg-[#38161c] text-[#fca5a5] border-[#ef4444]/40";
    return "bg-[#0b3552] text-[#7dd3fc] border-[#0ea5e9]/50";
}
function Badge({ children, className = "" }) {
    return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wide ${className}`}>{children}</span>;
}
function KpiCard({ title, value, subtitle, tone = "default", onClick, active = false }) {
    const tones = {
        default: "border-[#0b4d78] bg-[#08233D]",
        blue: "border-[#0ea5e9]/45 bg-[#0b2740]",
        red: "border-[#ef4444]/40 bg-[#2b1117]",
        amber: "border-[#d4af37]/45 bg-[#3a3110]",
        green: "border-[#10b981]/40 bg-[#0a332e]",
        slate: "border-[#24537a] bg-[#08233D]",
        purple: "border-[#8b5cf6]/40 bg-[#24163d]",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left rounded-[22px] border p-4 shadow-[0_12px_30px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:border-[#d4af37] ${tones[tone] || tones.default} ${active ? "ring-1 ring-[#d4af37]" : ""}`}
        >
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8fb9da]">{title}</div>
            <div className="text-3xl font-black text-white mt-2">{value}</div>
            {subtitle ? <div className="text-[10px] text-[#9fc4df] mt-1">{subtitle}</div> : null}
        </button>
    );
}
function GridRowsProStyle() {
    return <style>{`
    .agenda-pro { color:#f5fbff; }
    .agenda-pro select option { background:#061a2f; color:white; }
    .agenda-pro table { border-collapse: separate; border-spacing: 0; background:#061a2f; }
    .agenda-pro table tbody tr { transition: background .18s ease, box-shadow .18s ease; background:#08233D !important; }
    .agenda-pro table tbody tr:nth-child(even) { background:#092A49 !important; }
    .agenda-pro table tbody tr:hover { background:rgba(56,189,248,.075) !important; box-shadow: inset 3px 0 0 #d4af37; }
    .agenda-pro table tbody td { border-top:1px solid #24587C; background:transparent !important; color:#f5fbff; }
    .agenda-pro table thead th { position:sticky; top:0; z-index:10; background:#061A2F !important; color:#8fb9da; border-bottom:1px solid #24587C; text-transform:uppercase; letter-spacing:.13em; }
    .agenda-pro .row-main { font-weight: 700; color: #ffffff; }
    .agenda-pro .row-sub { color: #9fc4df; font-size: 11px; }
    .agenda-pro .input-pro { width: 100%; border: 1px solid #0b5f91; background:#0b2740; color:#fff; border-radius: .85rem; padding: .65rem .75rem; font-size: 12px; outline: none; }
    .agenda-pro .input-pro::placeholder { color:#7ea8c7; }
    .agenda-pro .input-pro:focus { border-color: #d4af37; box-shadow: 0 0 0 2px rgba(212,175,55,.18); }
    .agenda-pro .label-pro { display:block; font-size: 11px; color:#8fb9da; margin-bottom:6px; font-weight:700; text-transform:uppercase; letter-spacing:.14em; }
    .agenda-pro .dual-scroll-top,
    .agenda-pro .dual-scroll-bottom {
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-gutter: stable;
    }
    .agenda-pro .dual-scroll-top {
        height: 14px;
        border: 1px solid #24587C;
        border-radius:999px;
        background: #061A2F;
        margin: 0 12px 8px 12px;
    }
    .agenda-pro .dual-scroll-bottom {
        max-height: 68vh;
        overflow:auto;
    }
    .agenda-pro .dual-scroll-spacer {
        height: 1px;
        min-width: 1550px;
    }
    .agenda-pro .dual-scroll-top::-webkit-scrollbar,
    .agenda-pro .dual-scroll-bottom::-webkit-scrollbar { height:10px; width:10px; }
    .agenda-pro .dual-scroll-top::-webkit-scrollbar-track,
    .agenda-pro .dual-scroll-bottom::-webkit-scrollbar-track { background:#061a2f; border-radius:999px; }
    .agenda-pro .dual-scroll-top::-webkit-scrollbar-thumb,
    .agenda-pro .dual-scroll-bottom::-webkit-scrollbar-thumb { background:#2b638e; border-radius:999px; }
    .agenda-pro .dual-scroll-top::-webkit-scrollbar-thumb:hover,
    .agenda-pro .dual-scroll-bottom::-webkit-scrollbar-thumb:hover { background:#d4af37; }
    .agenda-pro .agenda-hero{background:linear-gradient(135deg,#03111f 0%,#031a2f 55%,#04223d 100%);border:1px solid rgba(11,93,140,.8);box-shadow:0 20px 45px rgba(0,0,0,.28)}
    .agenda-pro .agenda-card{border:1px solid rgba(11,93,140,.82);background:#04192b;box-shadow:0 12px 30px rgba(0,0,0,.24)}
    .agenda-pro .agenda-actions-bar{background:#031425;border:1px solid #0b4d78;border-radius:22px}
    .agenda-pro .agenda-btn{border:1px solid #24537a;background:#0b2740;color:#f5fbff;border-radius:999px;padding:10px 14px;font-size:12px;font-weight:700}
    .agenda-pro .agenda-btn:hover{background:#10324f;border-color:#d4af37}
    .agenda-pro .agenda-btn-primary{background:#d4af37;color:#081a2b;border-color:#d4af37}
    .agenda-pro .agenda-btn-primary:hover{background:#e8c457}
    .agenda-pro .agenda-table-action{border:1px solid #24537a;background:#0b2740;color:#f5fbff;border-radius:12px;padding:6px 10px;font-size:11px;font-weight:600}
    .agenda-pro .agenda-table-action:hover{background:#10324f;border-color:#d4af37}
    .agenda-pro .agenda-table-action-success{border-color:rgba(16,185,129,.4);background:#0a332e;color:#7cebd0}
    .agenda-pro .agenda-table-action-gold{border-color:rgba(212,175,55,.45);background:#3a3110;color:#f6d76b}
    .agenda-pro .agenda-modal-panel{background:#04192b;border:1px solid #0b5f91;color:#fff;box-shadow:0 20px 60px rgba(0,0,0,.35)}
    .agenda-pro .agenda-modal-header{background:#031425;border-bottom:1px solid #0b4d78}
    .agenda-pro .agenda-empty{background:#08233d;border:1px dashed #0b4d78;color:#9fc4df;border-radius:18px}
  `}</style>;
}

export default function AgendaPage() {
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [summary, setSummary] = useState({});
    const [byStage, setByStage] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [pipelines, setPipelines] = useState([]);
    const [stages, setStages] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [toast, setToast] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("create");
    const [form, setForm] = useState(EMPTY_FORM);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [completeForm, setCompleteForm] = useState(EMPTY_COMPLETE);
    const [viewMode, setViewMode] = useState("all");
    const [filters, setFilters] = useState({
        q: "", date_from: addDaysYmd(-30), date_to: addDaysYmd(60), status: "", type: "", channel: "",
        campaign_id: "", pipeline_id: "", pipeline_stage_id: "", product_line: "", urgency: "", account_id: "", opportunity_id: "",
    });
    const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });
    const topScrollRef = React.useRef(null);
    const bottomScrollRef = React.useRef(null);

    async function loadCatalogs() {
        const [accData, oppData, campData, pipeData, stageData] = await Promise.all([
            safeJson(`${API_BASE}/accounts_list.php`),
            safeJson(`${API_BASE}/opportunities_list.php`),
            safeJson(`${API_BASE}/campaigns_list.php?active_only=1`),
            safeJson(`${API_BASE}/pipelines_list.php?active_only=1`),
            safeJson(`${API_BASE}/pipeline_stages_list.php`),
        ]);
        setAccounts(asArray(accData));
        setOpportunities(asArray(oppData));
        setCampaigns(asArray(campData));
        setPipelines(asArray(pipeData));
        setStages(asArray(stageData));
    }
    function buildQuery(extra = {}) {
        const params = new URLSearchParams();
        const merged = { ...filters, ...extra };
        Object.entries(merged).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") params.set(key, value);
        });
        return params.toString();
    }
    async function loadData(extraFilters = {}) {
        try {
            setLoading(true);
            setError("");
            const query = buildQuery(extraFilters);
            const [activitiesData, summaryData] = await Promise.all([
                safeJson(`${API_BASE}/activities_list.php?${query}`),
                safeJson(`${API_BASE}/agenda_commercial_summary.php?${query}`),
            ]);
            setActivities(asArray(activitiesData));
            setSummary(summaryData.summary || {});
            setByStage(Array.isArray(summaryData.by_stage) ? summaryData.by_stage : []);
        } catch (err) {
            console.error(err);
            setError(err.message || "No se pudo cargar la agenda comercial.");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        async function boot() {
            try {
                setLoading(true);
                await loadCatalogs();

                const params = new URLSearchParams(window.location.search);
                const urlFilters = {};
                if (params.get("account_id")) urlFilters.account_id = params.get("account_id");
                if (params.get("opportunity_id")) urlFilters.opportunity_id = params.get("opportunity_id");
                if (params.get("campaign_id")) urlFilters.campaign_id = params.get("campaign_id");
                if (params.get("pipeline_id")) urlFilters.pipeline_id = params.get("pipeline_id");
                if (params.get("product_line")) urlFilters.product_line = params.get("product_line");

                if (Object.keys(urlFilters).length > 0) {
                    setFilters((prev) => ({ ...prev, ...urlFilters }));
                }

                await loadData(urlFilters);

                if (params.get("new_activity") === "1") {
                    setTimeout(() => {
                        openCreateModal({
                            account_id: urlFilters.account_id || "",
                            opportunity_id: urlFilters.opportunity_id || "",
                            type: "seguimiento_propuesta",
                            channel: "whatsapp",
                            title: "Seguimiento comercial",
                            objective: "Dar seguimiento comercial a la oportunidad.",
                        });
                    }, 250);
                }
            } catch (err) {
                console.error(err);
                setError(err.message || "No se pudo cargar la agenda comercial.");
                setLoading(false);
            }
        }
        boot();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadContactsForAccount(accountId, q = "") {
        if (!accountId) { setContacts([]); return; }
        try {
            const params = new URLSearchParams();
            params.set("account_id", accountId);
            params.set("limit", "80");
            if (q) params.set("q", q);
            const data = await safeJson(`${API_BASE}/contacts_search.php?${params.toString()}`);
            setContacts(asArray(data));
        } catch (err) {
            console.error(err);
            setContacts([]);
        }
    }
    const filteredOpportunitiesForForm = useMemo(() => {
        if (!form.account_id) return opportunities;
        return opportunities.filter((o) => String(o.account_id) === String(form.account_id));
    }, [opportunities, form.account_id]);
    const filteredStages = useMemo(() => {
        if (!filters.pipeline_id) return stages;
        return stages.filter((s) => String(s.pipeline_id) === String(filters.pipeline_id));
    }, [stages, filters.pipeline_id]);
    const filteredActivities = useMemo(() => {
        if (viewMode === "all") return activities;
        return activities.filter((act) => {
            if (viewMode === "today") return Number(act.is_today) === 1;
            if (viewMode === "overdue") return Number(act.is_overdue) === 1;
            if (viewMode === "next7") {
                const d = new Date(String(act.start_datetime).replace(" ", "T"));
                if (Number.isNaN(d.getTime())) return false;
                const now = new Date(); const next = new Date(); next.setDate(now.getDate() + 7);
                return d >= now && d <= next;
            }
            if (viewMode === "demos") return ["demo_orbis", "demo", "reunion_virtual", "reunion_presencial"].includes(String(act.type));
            if (viewMode === "proposals") return ["envio_propuesta", "seguimiento_propuesta"].includes(String(act.type));
            if (viewMode === "no_opp") return !act.opportunity_id;
            return true;
        });
    }, [activities, viewMode]);

    const sortedActivities = useMemo(() => {
        const rows = [...filteredActivities];
        const direction = sortConfig.direction === "asc" ? 1 : -1;

        rows.sort((a, b) => {
            const av = getActivitySortValue(a, sortConfig.key);
            const bv = getActivitySortValue(b, sortConfig.key);

            if (typeof av === "number" || typeof bv === "number") {
                return ((Number(av) || 0) - (Number(bv) || 0)) * direction;
            }

            return String(av).localeCompare(String(bv), "es", { numeric: true, sensitivity: "base" }) * direction;
        });

        return rows;
    }, [filteredActivities, sortConfig]);

    function requestSort(key) {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    }

    function SortTh({ sortKey, children, className = "" }) {
        const active = sortConfig.key === sortKey;
        return (
            <th className={`px-3 py-2 ${className}`}>
                <button
                    type="button"
                    onClick={() => requestSort(sortKey)}
                    className={`inline-flex items-center gap-1 hover:text-[#d4af37] ${active ? "text-[#8fd8ff]" : ""}`}
                    title="Ordenar columna con un click"
                >
                    <span>{children}</span>
                    <span className="text-[10px]">{active ? (sortConfig.direction === "asc" ? "▲" : "▼") : "↕"}</span>
                </button>
            </th>
        );
    }

    function handleFilterChange(e) {
        const { name, value } = e.target;
        setFilters((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "pipeline_id") next.pipeline_stage_id = "";
            if (name === "account_id") next.opportunity_id = "";
            return next;
        });
    }
    async function applyFilters() { await loadData(); }
    function clearFilters() {
        const reset = { q: "", date_from: addDaysYmd(-30), date_to: addDaysYmd(60), status: "", type: "", channel: "", campaign_id: "", pipeline_id: "", pipeline_stage_id: "", product_line: "", urgency: "", account_id: "", opportunity_id: "" };
        setFilters(reset); setViewMode("all"); loadData(reset);
    }
    function openCreateModal(prefill = {}) {
        const now = new Date(); now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        const localNow = now.toISOString().slice(0, 16);
        const nextForm = { ...EMPTY_FORM, start_datetime: localNow, ...prefill };
        setForm(nextForm); setModalMode("create"); setShowModal(true);
        if (nextForm.account_id) loadContactsForAccount(nextForm.account_id);
    }
    function openEditModal(act) {
        setForm({
            id: act.id, account_id: act.account_id ? String(act.account_id) : "", contact_id: act.contact_id ? String(act.contact_id) : "", opportunity_id: act.opportunity_id ? String(act.opportunity_id) : "",
            type: act.type || "seguimiento_propuesta", channel: act.channel || "whatsapp", location: act.location || "", title: act.title || "", objective: act.objective || "", result: act.result || "", status: act.status || "programada",
            start_datetime: toDateTimeLocal(act.start_datetime), end_datetime: toDateTimeLocal(act.end_datetime), next_action: act.next_action || "", next_action_date: toDateTimeLocal(act.next_action_date),
            direct_cost: act.direct_cost || "", time_spent_hours: act.time_spent_hours || "", notes: act.notes || "", alerts: [],
        });
        setModalMode("edit"); setShowModal(true);
        if (act.account_id) loadContactsForAccount(act.account_id);
    }
    function handleFormChange(e) {
        const { name, value } = e.target;
        setForm((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "account_id") { next.contact_id = ""; next.opportunity_id = ""; loadContactsForAccount(value); }
            if (name === "opportunity_id") {
                const opp = opportunities.find((o) => String(o.id) === String(value));
                if (opp && !next.account_id) next.account_id = String(opp.account_id || "");
                if (opp && !next.title) next.title = `Seguimiento — ${opp.name}`;
                if (opp && !next.objective) next.objective = opp.next_action || "Dar seguimiento comercial a la oportunidad.";
            }
            if (name === "type") {
                const label = labelFromOptions(TYPE_OPTIONS, value);
                if (!next.title || next.title.startsWith("Seguimiento") || next.title.startsWith("Actividad")) next.title = label;
            }
            return next;
        });
    }
    async function handleSaveActivity(e) {
        e.preventDefault(); setError(""); setToast(null);
        if (!form.account_id) return setError("Selecciona una cuenta.");
        if (!form.title.trim()) return setError("El título de la actividad es obligatorio.");
        if (!form.start_datetime) return setError("La fecha/hora de inicio es obligatoria.");
        try {
            setSaving(true);
            const payload = { ...form, id: form.id ? Number(form.id) : null, account_id: Number(form.account_id), contact_id: form.contact_id ? Number(form.contact_id) : null, opportunity_id: form.opportunity_id ? Number(form.opportunity_id) : null, start_datetime: normalizeDateTimeForApi(form.start_datetime), end_datetime: normalizeDateTimeForApi(form.end_datetime), next_action_date: normalizeDateTimeForApi(form.next_action_date), direct_cost: form.direct_cost !== "" ? Number(form.direct_cost) : null, time_spent_hours: form.time_spent_hours !== "" ? Number(form.time_spent_hours) : null, created_by: null };
            const endpoint = modalMode === "edit" ? `${API_BASE}/activity_update.php` : `${API_BASE}/activity_create.php`;
            await safeJson(endpoint, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
            setShowModal(false); setToast({ type: "ok", msg: modalMode === "edit" ? "Actividad actualizada." : "Actividad creada." }); await loadData();
        } catch (err) { console.error(err); setError(err.message || "No se pudo guardar la actividad."); }
        finally { setSaving(false); }
    }
    function openCompleteModal(act) {
        setCompleteForm({ ...EMPTY_COMPLETE, id: act.id, result: act.result || "", next_action: act.next_action || act.opportunity_next_action || "", next_action_date: toDateTimeLocal(act.next_action_date || act.opportunity_next_action_date) });
        setShowCompleteModal(true);
    }
    async function handleQuickComplete(e) {
        e.preventDefault(); setError(""); setToast(null);
        if (!completeForm.result.trim()) return setError("Registra el resultado antes de marcar la actividad como realizada.");
        try {
            setSaving(true);
            await safeJson(`${API_BASE}/activity_quick_complete.php`, { method: "POST", headers: authHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ ...completeForm, id: Number(completeForm.id), next_action_date: normalizeDateTimeForApi(completeForm.next_action_date) }) });
            setShowCompleteModal(false); setToast({ type: "ok", msg: "Actividad marcada como realizada." }); await loadData();
        } catch (err) { console.error(err); setError(err.message || "No se pudo completar la actividad."); }
        finally { setSaving(false); }
    }
    const filteredOppsForFilter = useMemo(() => {
        if (!filters.account_id) return opportunities;
        return opportunities.filter((o) => String(o.account_id) === String(filters.account_id));
    }, [opportunities, filters.account_id]);

    function syncHorizontalScroll(source) {
        const top = topScrollRef.current;
        const bottom = bottomScrollRef.current;
        if (!top || !bottom) return;

        if (source === "top" && bottom.scrollLeft !== top.scrollLeft) {
            bottom.scrollLeft = top.scrollLeft;
        }

        if (source === "bottom" && top.scrollLeft !== bottom.scrollLeft) {
            top.scrollLeft = bottom.scrollLeft;
        }
    }

    return (
        <MainLayout>
            <GridRowsProStyle />
            <div className="agenda-pro min-h-screen bg-[#041426] px-3 py-6 md:px-6 md:py-8"><div className="mx-auto max-w-[1500px] space-y-6">
                <div className="agenda-hero rounded-[28px] px-6 py-6 md:px-7 md:py-7 flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                    <div>
                        <p className="mb-3 inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]"><span className="h-px w-10 bg-[#D4AF37]" />CRM Comercial</p><h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">Agenda Comercial PRO</h2>
                        <p className="text-sm md:text-base text-[#d7e8f6] max-w-3xl leading-relaxed">Seguimiento diario de oportunidades, demos, propuestas, contactos y siguientes acciones comerciales.</p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                        <button type="button" onClick={() => navigate("/opportunities")} className="agenda-btn">Oportunidades</button>
                        <button type="button" onClick={() => loadData()} className="agenda-btn">Recargar</button>
                        <button type="button" onClick={() => openCreateModal()} className="agenda-btn agenda-btn-primary">+ Nueva actividad</button>
                    </div>
                </div>
                {toast && <div className={`text-xs rounded-xl px-4 py-3 border ${toast.type === "ok" ? "bg-[#0a332e] text-[#86efac] border-[#10b981]/40" : "bg-[#2b1117] text-[#fca5a5] border-[#ef4444]/40"}`}>{toast.msg}</div>}
                {error && <div className="text-xs rounded-xl px-4 py-3 border bg-[#2b1117] text-[#fca5a5] border-[#ef4444]/40">{error}</div>}

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                    <KpiCard title="Actividades hoy" value={summary.today_activities || 0} subtitle="Operación del día" tone="blue" active={viewMode === "today"} onClick={() => setViewMode("today")} />
                    <KpiCard title="Vencidas" value={summary.overdue_activities || 0} subtitle="Requieren atención" tone="red" active={viewMode === "overdue"} onClick={() => setViewMode("overdue")} />
                    <KpiCard title="Próximos 7 días" value={summary.next_7_days || 0} subtitle="Seguimientos próximos" tone="amber" active={viewMode === "next7"} onClick={() => setViewMode("next7")} />
                    <KpiCard title="Demos / reuniones" value={summary.demos_or_meetings || 0} subtitle="Presentaciones y citas" tone="purple" active={viewMode === "demos"} onClick={() => setViewMode("demos")} />
                    <KpiCard title="Sin próxima acción" value={summary.opportunities_without_next_action || 0} subtitle="Oportunidades por ordenar" tone="slate" onClick={() => navigate("/opportunities")} />
                    <KpiCard title="Propuestas" value={summary.proposal_followups || 0} subtitle="Seguimiento de propuesta" tone="green" active={viewMode === "proposals"} onClick={() => setViewMode("proposals")} />
                    <KpiCard title="Alta urgencia" value={summary.high_urgency_activities || 0} subtitle="Oportunidades calientes" tone="red" onClick={() => setFilters((p) => ({ ...p, urgency: "alta" }))} />
                    <KpiCard title="Valor piloto" value={formatCurrency(summary.pilot_value_in_followup || 0)} subtitle="En seguimiento" tone="amber" />
                    <KpiCard title="Potencial mensual" value={formatCurrency(summary.monthly_potential_in_followup || 0)} subtitle="Continuidad" tone="green" />
                    <KpiCard title="Sin oportunidad" value={activities.filter((a) => !a.opportunity_id).length} subtitle="Actividades sueltas" tone="slate" active={viewMode === "no_opp"} onClick={() => setViewMode("no_opp")} />
                </div>

                <div className="agenda-card rounded-[24px] p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        <div><label className="label-pro">Buscar</label><input name="q" value={filters.q} onChange={handleFilterChange} className="input-pro" placeholder="Actividad, cuenta, contacto..." /></div>
                        <div><label className="label-pro">Desde</label><input type="date" name="date_from" value={filters.date_from} onChange={handleFilterChange} className="input-pro" /></div>
                        <div><label className="label-pro">Hasta</label><input type="date" name="date_to" value={filters.date_to} onChange={handleFilterChange} className="input-pro" /></div>
                        <div><label className="label-pro">Estado</label><select name="status" value={filters.status} onChange={handleFilterChange} className="input-pro"><option value="">Todos</option>{STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
                        <div><label className="label-pro">Tipo</label><select name="type" value={filters.type} onChange={handleFilterChange} className="input-pro"><option value="">Todos</option>{TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
                        <div><label className="label-pro">Canal</label><select name="channel" value={filters.channel} onChange={handleFilterChange} className="input-pro"><option value="">Todos</option>{CHANNEL_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
                        <div><label className="label-pro">Producto</label><select name="product_line" value={filters.product_line} onChange={handleFilterChange} className="input-pro">{PRODUCT_LINES.map((line) => <option key={line || "all"} value={line}>{line || "Todos"}</option>)}</select></div>
                        <div><label className="label-pro">Campaña</label><select name="campaign_id" value={filters.campaign_id} onChange={handleFilterChange} className="input-pro"><option value="">Todas</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
                        <div><label className="label-pro">Pipeline</label><select name="pipeline_id" value={filters.pipeline_id} onChange={handleFilterChange} className="input-pro"><option value="">Todos</option>{pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                        <div><label className="label-pro">Etapa</label><select name="pipeline_stage_id" value={filters.pipeline_stage_id} onChange={handleFilterChange} className="input-pro"><option value="">Todas</option>{filteredStages.map((s) => <option key={s.id} value={s.id}>{s.stage_order}. {s.name}</option>)}</select></div>
                        <div><label className="label-pro">Urgencia</label><select name="urgency" value={filters.urgency} onChange={handleFilterChange} className="input-pro">{URGENCY_OPTIONS.map(([value, label]) => <option key={value || "all"} value={value}>{label}</option>)}</select></div>
                        <div><label className="label-pro">Cuenta</label><select name="account_id" value={filters.account_id} onChange={handleFilterChange} className="input-pro"><option value="">Todas</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
                        <div className="md:col-span-2"><label className="label-pro">Oportunidad</label><select name="opportunity_id" value={filters.opportunity_id} onChange={handleFilterChange} className="input-pro"><option value="">Todas</option>{filteredOppsForFilter.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
                        <div className="flex items-end gap-2"><button type="button" onClick={applyFilters} className="agenda-btn agenda-btn-primary">Aplicar</button><button type="button" onClick={clearFilters} className="agenda-btn">Limpiar</button></div>
                    </div>
                    <div className="agenda-actions-bar p-3 flex flex-wrap gap-2 text-[11px]">
                        {[["all", "Todo"], ["today", "Hoy"], ["overdue", "Vencidas"], ["next7", "Próximos 7 días"], ["demos", "Demos / reuniones"], ["proposals", "Propuestas"], ["no_opp", "Sin oportunidad"]].map(([value, label]) => <button key={value} type="button" onClick={() => setViewMode(value)} className={`agenda-btn ${viewMode === value ? "agenda-btn-primary" : ""}`}>{label}</button>)}
                    </div>
                </div>

                <div className="agenda-card rounded-[24px] overflow-hidden">
                    <div className="px-5 py-4 border-b border-[#0b4d78] flex items-center justify-between bg-[#031425]"><div><h3 className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#d4af37]">Seguimiento comercial</h3><p className="text-[11px] text-[#9fc4df]">{sortedActivities.length} actividad(es). Mueve oportunidades hacia demo, propuesta, piloto y continuidad.</p></div></div>
                    <div
                        ref={topScrollRef}
                        className="dual-scroll-top"
                        onScroll={() => syncHorizontalScroll("top")}
                        aria-hidden="true"
                    >
                        <div className="dual-scroll-spacer" />
                    </div>
                    <div
                        ref={bottomScrollRef}
                        className="dual-scroll-bottom"
                        onScroll={() => syncHorizontalScroll("bottom")}
                    >
                        <table className="min-w-[1550px] w-full text-xs"><thead><tr className="text-left text-[11px] font-semibold"><SortTh sortKey="date" className="w-[150px]">Fecha / estado</SortTh><SortTh sortKey="type" className="w-[160px]">Tipo / canal</SortTh><SortTh sortKey="account" className="w-[230px]">Cuenta / contacto</SortTh><SortTh sortKey="opportunity" className="w-[260px]">Oportunidad</SortTh><SortTh sortKey="campaign" className="w-[210px]">Campaña / pipeline</SortTh><SortTh sortKey="value" className="w-[150px]">Valor</SortTh><SortTh sortKey="activity" className="w-[260px]">Actividad / próximo paso</SortTh><th className="px-3 py-2 w-[230px] text-right">Acciones</th></tr></thead><tbody>
                            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-[#9fc4df]">Cargando agenda comercial.</td></tr>}
                            {!loading && sortedActivities.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-[#8fb9da]">No hay actividades con estos filtros.</td></tr>}
                            {!loading && sortedActivities.map((act) => <tr key={act.id} className=""><td className="px-3 py-2"><div className="row-main">{formatDateTime(act.start_datetime)}</div><div className="flex flex-wrap gap-1 mt-1"><Badge className={statusTone(act.status)}>{labelFromOptions(STATUS_OPTIONS, act.status)}</Badge>{Number(act.is_overdue) === 1 ? <Badge className="bg-[#38161c] text-[#fca5a5] border-[#ef4444]/40">Vencida</Badge> : null}</div></td><td className="px-3 py-2"><div className="row-main">{labelFromOptions(TYPE_OPTIONS, act.type)}</div><div className="row-sub">{labelFromOptions(CHANNEL_OPTIONS, act.channel)}</div>{act.location ? <div className="row-sub mt-1">{act.location}</div> : null}</td><td className="px-3 py-2"><div className="row-main">{act.account_name || "Sin cuenta"}</div><div className="row-sub">{act.contact_name || "Sin contacto vinculado"}</div>{act.contact_mobile || act.contact_phone ? <div className="row-sub">{act.contact_mobile || act.contact_phone}</div> : null}</td><td className="px-3 py-2">{act.opportunity_id ? <><button type="button" onClick={() => navigate(`/opportunities/${act.opportunity_id}/edit`)} className="row-main text-left hover:text-[#d4af37]">{act.opportunity_name}</button><div className="row-sub">{act.product_line || "Sin producto"}</div><div className="flex flex-wrap gap-1 mt-1">{act.pipeline_stage_name ? <Badge className={stageTone(act.stage_type)}>{act.pipeline_stage_name}</Badge> : null}{act.urgency ? <Badge className={urgencyTone(act.urgency)}>{act.urgency}</Badge> : null}</div></> : <div><div className="row-main text-[#9fc4df]">Sin oportunidad vinculada</div><button type="button" onClick={() => openEditModal(act)} className="mt-1 text-[11px] text-[#8fd8ff] hover:underline">Vincular oportunidad</button></div>}</td><td className="px-3 py-2"><div className="text-[11px] text-[#d9e8f5]">{act.campaign_name || "Sin campaña"}</div><div className="row-sub">{act.pipeline_name || "Sin pipeline"}</div></td><td className="px-3 py-2"><div className="text-[11px] text-[#d9e8f5]">Piloto: {formatCurrency(act.pilot_value, act.currency)}</div><div className="row-sub">Mensual: {formatCurrency(act.monthly_potential, act.currency)}</div></td><td className="px-3 py-2"><div className="row-main">{act.title}</div>{act.objective ? <div className="row-sub mt-1">Objetivo: {act.objective}</div> : null}{act.next_action ? <div className="text-[11px] text-[#d9e8f5] mt-1">Próx.: {act.next_action}{act.next_action_date ? <span className="text-[#9fc4df]"> · {formatDateTime(act.next_action_date)}</span> : null}</div> : <div className="row-sub mt-1">Sin próximo paso registrado.</div>}</td><td className="px-3 py-2 text-right"><div className="flex flex-wrap justify-end gap-1"><button type="button" onClick={() => openCompleteModal(act)} className="agenda-table-action agenda-table-action-success">Realizada</button><button type="button" onClick={() => openEditModal(act)} className="agenda-table-action">Editar</button><button
                                type="button"
                                onClick={() => openGoogleCalendarEvent(act, setError)}
                                className="agenda-table-action agenda-table-action-gold"
                                title="Abrir esta actividad en Google Calendar"
                            >
                                Google Cal
                            </button>{act.opportunity_id ? <button type="button" onClick={() => navigate(`/opportunities/${act.opportunity_id}/edit`)} className="agenda-table-action">Abrir opp.</button> : null}</div></td></tr>)}
                        </tbody></table></div>
                </div>

                {byStage.length > 0 && <div className="agenda-card rounded-[24px] overflow-hidden"><div className="px-4 py-3 border-b border-[#0b4d78]"><h3 className="text-xs font-semibold text-white">Lectura por etapa comercial</h3><p className="text-[10px] text-[#9fc4df]">Resumen de oportunidades por etapa del pipeline.</p></div><div className="overflow-x-auto"><table className="min-w-full text-xs"><thead><tr className="text-left text-[11px] font-semibold"><th className="px-3 py-2">Etapa</th><th className="px-3 py-2">Tipo</th><th className="px-3 py-2 text-right">Oportunidades</th><th className="px-3 py-2 text-right">Valor piloto</th><th className="px-3 py-2 text-right">Potencial mensual</th></tr></thead><tbody>{byStage.map((row, idx) => <tr key={`${row.stage_id || "na"}-${idx}`} className=""><td className="px-3 py-2"><span className="inline-block h-2.5 w-2.5 rounded-full mr-2" style={{ background: row.color_hex || "#94A3B8" }} />{row.stage_name || "Sin etapa"}</td><td className="px-3 py-2">{row.stage_type || "—"}</td><td className="px-3 py-2 text-right">{row.opportunities_count || 0}</td><td className="px-3 py-2 text-right">{formatCurrency(row.pilot_value)}</td><td className="px-3 py-2 text-right">{formatCurrency(row.monthly_potential)}</td></tr>)}</tbody></table></div></div>}

                {showModal && <ActivityModal modalMode={modalMode} form={form} setForm={setForm} accounts={accounts} contacts={contacts} filteredOpportunitiesForForm={filteredOpportunitiesForForm} saving={saving} onClose={() => setShowModal(false)} onSubmit={handleSaveActivity} onChange={handleFormChange} />}
                {showCompleteModal && <CompleteModal completeForm={completeForm} setCompleteForm={setCompleteForm} saving={saving} onClose={() => setShowCompleteModal(false)} onSubmit={handleQuickComplete} />}
            </div>
            </div>
        </MainLayout>
    );
}

function ActivityModal({ modalMode, form, accounts, contacts, filteredOpportunitiesForForm, saving, onClose, onSubmit, onChange }) {
    return <div className="fixed inset-0 bg-[#010b14]/75 backdrop-blur-sm flex items-center justify-center z-50 p-4"><div className="agenda-modal-panel rounded-[24px] w-full max-w-5xl max-h-[92vh] overflow-y-auto"><div className="agenda-modal-header px-5 py-4 flex items-start justify-between gap-3"><div><h3 className="text-base font-semibold text-white">{modalMode === "edit" ? "Editar actividad" : "Nueva actividad comercial"}</h3><p className="text-[11px] text-[#9fc4df]">Vincula cuenta, contacto y oportunidad para dar trazabilidad comercial.</p></div><button type="button" onClick={onClose} className="text-xs text-[#9fc4df] hover:text-[#d9e8f5]">Cerrar</button></div><form onSubmit={onSubmit} className="p-5 space-y-4 text-xs"><div className="grid grid-cols-1 md:grid-cols-3 gap-3"><div><label className="label-pro">Cuenta *</label><select name="account_id" value={form.account_id} onChange={onChange} className="input-pro"><option value="">— Seleccionar cuenta —</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div><div><label className="label-pro">Contacto</label><select name="contact_id" value={form.contact_id} onChange={onChange} className="input-pro"><option value="">— Sin contacto —</option>{contacts.map((c) => <option key={c.id} value={c.id}>{c.full_name || c.first_name} {c.position ? `· ${c.position}` : ""}</option>)}</select></div><div><label className="label-pro">Oportunidad</label><select name="opportunity_id" value={form.opportunity_id} onChange={onChange} className="input-pro"><option value="">— Sin oportunidad —</option>{filteredOpportunitiesForForm.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div><div><label className="label-pro">Tipo</label><select name="type" value={form.type} onChange={onChange} className="input-pro">{TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div><div><label className="label-pro">Canal</label><select name="channel" value={form.channel} onChange={onChange} className="input-pro">{CHANNEL_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div><div><label className="label-pro">Estado</label><select name="status" value={form.status} onChange={onChange} className="input-pro">{STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div><div className="md:col-span-2"><label className="label-pro">Título *</label><input name="title" value={form.title} onChange={onChange} className="input-pro" /></div><div><label className="label-pro">Ubicación / enlace</label><input name="location" value={form.location} onChange={onChange} className="input-pro" /></div><div><label className="label-pro">Inicio *</label><input type="datetime-local" name="start_datetime" value={form.start_datetime} onChange={onChange} className="input-pro" /></div><div><label className="label-pro">Fin</label><input type="datetime-local" name="end_datetime" value={form.end_datetime} onChange={onChange} className="input-pro" /></div><div><label className="label-pro">Horas invertidas</label><input type="number" min="0" step="0.25" name="time_spent_hours" value={form.time_spent_hours} onChange={onChange} className="input-pro" /></div><div className="md:col-span-3"><label className="label-pro">Objetivo</label><textarea name="objective" value={form.objective} onChange={onChange} rows={2} className="input-pro resize-none" /></div><div className="md:col-span-3"><label className="label-pro">Resultado</label><textarea name="result" value={form.result} onChange={onChange} rows={2} className="input-pro resize-none" /></div><div className="md:col-span-2"><label className="label-pro">Próxima acción</label><input name="next_action" value={form.next_action} onChange={onChange} className="input-pro" /></div><div><label className="label-pro">Fecha próxima acción</label><input type="datetime-local" name="next_action_date" value={form.next_action_date} onChange={onChange} className="input-pro" /></div><div className="md:col-span-3"><label className="label-pro">Notas</label><textarea name="notes" value={form.notes} onChange={onChange} rows={3} className="input-pro resize-none" /></div></div><div className="agenda-actions-bar p-3 flex justify-end gap-2"><button type="button" onClick={onClose} className="agenda-table-action" disabled={saving}>Cancelar</button><button type="submit" className="agenda-table-action agenda-table-action-gold disabled:opacity-60" disabled={saving}>{saving ? "Guardando..." : modalMode === "edit" ? "Guardar cambios" : "Guardar actividad"}</button></div></form></div></div>;
}

function CompleteModal({ completeForm, setCompleteForm, saving, onClose, onSubmit }) {
    return <div className="fixed inset-0 bg-[#010b14]/75 backdrop-blur-sm flex items-center justify-center z-50 p-4"><div className="agenda-modal-panel rounded-[24px] w-full max-w-2xl"><div className="agenda-modal-header px-5 py-4 flex items-start justify-between gap-3"><div><h3 className="text-base font-semibold text-white">Marcar actividad como realizada</h3><p className="text-[11px] text-[#9fc4df]">Registra resultado y define el siguiente paso.</p></div><button type="button" onClick={onClose} className="text-xs text-[#9fc4df] hover:text-[#d9e8f5]">Cerrar</button></div><form onSubmit={onSubmit} className="p-5 space-y-3 text-xs"><div><label className="label-pro">Resultado *</label><textarea value={completeForm.result} onChange={(e) => setCompleteForm((p) => ({ ...p, result: e.target.value }))} rows={3} className="input-pro resize-none" placeholder="Qué pasó, qué se acordó, qué respondió el prospecto..." /></div><div><label className="label-pro">Próxima acción</label><input value={completeForm.next_action} onChange={(e) => setCompleteForm((p) => ({ ...p, next_action: e.target.value }))} className="input-pro" /></div><div><label className="label-pro">Fecha próxima acción</label><input type="datetime-local" value={completeForm.next_action_date} onChange={(e) => setCompleteForm((p) => ({ ...p, next_action_date: e.target.value }))} className="input-pro" /></div><div><label className="label-pro">Notas adicionales</label><textarea value={completeForm.notes} onChange={(e) => setCompleteForm((p) => ({ ...p, notes: e.target.value }))} rows={2} className="input-pro resize-none" /></div><label className="inline-flex items-center gap-2 text-xs text-[#d9e8f5]"><input type="checkbox" checked={completeForm.create_next_activity} onChange={(e) => setCompleteForm((p) => ({ ...p, create_next_activity: e.target.checked }))} />Crear automáticamente la siguiente actividad</label>{completeForm.create_next_activity ? <div className="grid grid-cols-1 md:grid-cols-2 gap-3"><div><label className="label-pro">Tipo siguiente actividad</label><select value={completeForm.next_type} onChange={(e) => setCompleteForm((p) => ({ ...p, next_type: e.target.value }))} className="input-pro">{TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div><div><label className="label-pro">Canal siguiente actividad</label><select value={completeForm.next_channel} onChange={(e) => setCompleteForm((p) => ({ ...p, next_channel: e.target.value }))} className="input-pro">{CHANNEL_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div></div> : null}<div className="agenda-actions-bar p-3 flex justify-end gap-2"><button type="button" onClick={onClose} className="agenda-table-action" disabled={saving}>Cancelar</button><button type="submit" className="agenda-table-action agenda-table-action-success disabled:opacity-60" disabled={saving}>{saving ? "Guardando..." : "Marcar realizada"}</button></div></form></div></div>;
}
